import { useState } from "react";
import { toast } from "sonner";
import { EnvelopeSimple, X } from "@phosphor-icons/react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const DISMISS_KEY = "hc_verify_banner_dismissed";

/**
 * Soft-gate verification reminder. Renders sticky inside the shell when the
 * authed user has `email_verified === false`. Lets users resend the link
 * inline. Dismiss is per-session (localStorage) so we don't nag forever — the
 * gating on sensitive actions (Stripe checkout, job apply, listing create)
 * remains in place regardless.
 */
export default function VerifyEmailBanner() {
    const { user } = useAuth();
    const [dismissed, setDismissed] = useState(() => {
        try { return localStorage.getItem(DISMISS_KEY) === "1"; } catch { return false; }
    });
    const [sending, setSending] = useState(false);

    if (!user || user.email_verified !== false || dismissed) return null;

    const resend = async () => {
        setSending(true);
        try {
            const { data } = await api.post("/auth/resend-verification", {});
            if (data?.throttled) {
                toast.info("A fresh link was just sent — check your inbox.");
            } else {
                toast.success(`Verification link sent to ${user.email}`);
            }
        } catch {
            toast.error("Could not resend the verification email. Try again in a minute.");
        } finally {
            setSending(false);
        }
    };

    const dismiss = () => {
        try { localStorage.setItem(DISMISS_KEY, "1"); } catch { /* private mode */ }
        setDismissed(true);
    };

    return (
        <div
            role="status"
            data-testid="verify-email-banner"
            className="relative border-b border-amber-500/30 bg-amber-500/[0.06] text-amber-100"
        >
            <div className="max-w-[1920px] mx-auto px-4 sm:px-6 py-2.5 flex flex-wrap items-center gap-3">
                <EnvelopeSimple size={16} weight="duotone" className="text-amber-300 shrink-0" />
                <div className="flex-1 min-w-[200px] text-xs sm:text-sm">
                    <span className="font-bold text-amber-200">verify your email</span>
                    <span className="text-amber-100/80 hidden sm:inline"> — confirm <span className="font-mono text-amber-300">{user.email}</span> to unlock applications, listings & Pro.</span>
                </div>
                <button
                    onClick={resend}
                    disabled={sending}
                    className="text-[10px] uppercase tracking-widest font-bold border border-amber-400/40 hover:border-amber-300 hover:bg-amber-400/10 text-amber-200 px-3 py-1.5 rounded-sm transition-colors disabled:opacity-50"
                    data-testid="verify-email-resend"
                >
                    {sending ? "sending…" : "resend link"}
                </button>
                <button
                    onClick={dismiss}
                    aria-label="Dismiss"
                    className="text-amber-300/70 hover:text-amber-100 transition-colors"
                    data-testid="verify-email-dismiss"
                >
                    <X size={14} weight="bold" />
                </button>
            </div>
        </div>
    );
}
